import { CreditCardIcon } from "@heroicons/react/16/solid";
import type { FunctionComponent } from "react";

import Card from "~/components/Card/Card";
import HorizontalStats from "~/components/HorizontalStats/HorizontalStats";
import type { HorizontalStat } from "~/components/HorizontalStats/horizontalStatsTypes.ts";
import OverflowPercentageChart from "~/components/OverflowPercentageChart/OverflowPercentageChart";
import Tooltip from "~/components/Tooltip.tsx";
import type { ProjectRevenueReport } from "~/modules/project/api/projectRevenueReport/projectRevenueReportTypes.ts";
import {
	BILLED_AT_TARGET_COLOR,
	BILLED_BELOW_TARGET_COLOR,
	getBilledShare,
	getInvoicedTotalCents,
} from "~/modules/project/components/ProjectDetailsView/components/ProjectRevenueReportSection/projectRevenueReportUtils.ts";
import { formatCentsToCurrency } from "~/utils/currencyUtils.ts";

type Props = {
	report: ProjectRevenueReport;
};

const ProjectRevenueStatsCard: FunctionComponent<Props> = ({ report }) => {
	const billableTrackedCents = report.totalTrackedBillableCents;
	const { onQuotaCents, notBilledCents } = report.billingSplit;
	// Travel cost changes "Abgerechnet" only. The subline, "offen" and the ring never include it.
	const invoicedTotalCents = getInvoicedTotalCents(report);
	const { percentage, percentageLabel, isFullyBilled } = getBilledShare(report.billingSplit);
	const invoicedTooltip = report.quotas.length > 0
		? "Summe der Rechnungen dieses Projekts. Kontingentrechnungen zählen voll, auch wenn das Kontingent andere Projekte bedient."
		: "Summe der Rechnungen dieses Projekts.";

	const stats: HorizontalStat[] = [
		{
			label: "Volumen aus Erfassung",
			value: formatCentsToCurrency(billableTrackedCents),
			subValue: onQuotaCents !== 0 && <Tooltip text={`Davon ${formatCentsToCurrency(onQuotaCents)} über Kontingente abgerechnet.`}>
				<div className="flex items-center justify-center text-gray-500">
					<span>{formatCentsToCurrency(onQuotaCents, 0)}</span>
					<CreditCardIcon className="ml-1 size-3 text-gray-400" />
				</div>
			</Tooltip>,
		},
		{
			label: "Nicht abrechenbar",
			value: <span className="text-gray-400">{formatCentsToCurrency(report.totalTrackedNotBillableCents)}</span>,
		},
		{
			label: "Abgerechnet",
			value: <Tooltip text={invoicedTooltip} className="font-normal">
				<span>{formatCentsToCurrency(invoicedTotalCents)}</span>
			</Tooltip>,
		},
		{
			label: "Nicht abgerechnet (offen)",
			value: formatCentsToCurrency(notBilledCents),
		},
	];

	return <Card>
		<HorizontalStats stats={stats}>
			<OverflowPercentageChart percentage={percentage}
									 size={80}
									 thickness={0.4}
									 arcColor={isFullyBilled ? BILLED_AT_TARGET_COLOR : BILLED_BELOW_TARGET_COLOR}>
				<div className="text-sm font-bold">{percentageLabel}%</div>
			</OverflowPercentageChart>
		</HorizontalStats>
	</Card>;
};

export default ProjectRevenueStatsCard;
